import { useState } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { postFetcher, swrKey } from "@/lib/swr";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { useApps } from "@/hooks/apps";
import { applyScheduling, engagementAppIds } from "@/lib/engagement";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { States } from "@/constants/version-state";
import type { App, Prd, Version } from "@/types/domain";

// Link a PRD into one of an app's open versions (POST /prds/:id/schedule).
// `pid` fixed → app preselected and hidden (app overview Backlog rows);
// omitted → pick the app first, restricted to the PRD's related apps
// (PRD detail) — scheduling into an app the PRD has no relation with is not a thing.
export const ScheduleVersionDialog = ({
  prd,
  pid,
  triggerClassName,
  triggerLabel,
}: {
  prd: Prd;
  pid?: string;
  /** Override trigger sizing: rows use the h-6 default, headers pass h-7. */
  triggerClassName?: string;
  /** Override trigger text — e.g. "请求上车 at Maker" when the app context
      needs to be unmistakable (PRD detail header). */
  triggerLabel?: string;
}) => {
  const { toast } = useToast();
  const apps = useApps();
  const [open, setOpen] = useState(false);
  const [appId, setAppId] = useState(pid ?? "");
  const [versionId, setVersionId] = useState("");

  // Only apps the PRD already has a relation with are valid targets.
  const relatedIds = new Set(engagementAppIds(prd ?? {}));
  const appOptions: App[] = apps.filter((a: App) =>
    relatedIds.has(String(a.id))
  );

  // Scheduling targets = 规划中 (state 0) versions only — same rule as the
  // app pool rows. With a fixed pid the list loads eagerly so the trigger
  // can gray out when there is nothing to board (SWR key shared across
  // rows — one request).
  const { data: vdata } = useSWR<{ data: Version[]; total: number }>(
    pid
      ? swrKey("/versions", { pid, state: "0", pageSize: 100 })
      : open && appId
        ? swrKey("/versions", { pid: appId, state: "0", pageSize: 100 })
        : null
  );
  const versions = vdata?.data ?? [];
  const noPlanning = !!pid && !!vdata && versions.length === 0;

  const targetId = prd.engagements?.find(e => e.appId === (pid ?? appId))?.id ?? prd.id;
  const { trigger: link, isMutating } = useSWRMutation(`/prds/${targetId}/schedule`, postFetcher<Prd>, {
    onSuccess: (saved) => {
      setOpen(false);
      applyScheduling(prd.id, saved);
      toast({ title: "已上车", description: prd.docName });
    },
    onError: (e: unknown) => {
      toastError(toast, "上车失败", e, "Submit failed.");
    },
  });

  const onOpenChange = (o: boolean) => {
    if (o) {
      setAppId(pid ?? "");
      setVersionId("");
    }
    setOpen(o);
  };

  if (noPlanning) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            {/* span: a disabled button swallows no events — the wrapper keeps
                the tooltip alive and stops the click from bubbling. */}
            <span className="shrink-0" onClick={(e) => e.stopPropagation()}>
              <Button
                size="sm"
                disabled
                className={triggerClassName ?? "h-6 shrink-0 px-2 text-xs"}
              >
                {triggerLabel ?? "请求上车"}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>没有规划中的版本</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          className={triggerClassName ?? "h-6 shrink-0 px-2 text-xs"}
        >
          {triggerLabel ?? "请求上车"}
        </Button>
      </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          {/* min-w-0: DialogContent is a grid — without it the nowrap title
              widens the track past max-w-md instead of truncating. */}
          <DialogHeader className="min-w-0">
            <DialogTitle className="truncate pr-6">
              请求上车 “{prd.docName}”
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!pid && (
              <div className="space-y-1">
                <span className="text-sm text-muted-foreground">App</span>
                <Select
                  value={appId}
                  onValueChange={(val: string) => {
                    setAppId(val);
                    setVersionId("");
                  }}
                >
                  <SelectTrigger size="sm" className="w-full">
                    <SelectValue placeholder="Select app…" />
                  </SelectTrigger>
                  <SelectContent>
                    {appOptions.map((a) => (
                      <SelectItem key={a.id} value={String(a.id)}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {appOptions.length === 0 && (
                  <span className="text-xs text-muted-foreground">
                    No related apps — add one via “Apps” first.
                  </span>
                )}
              </div>
            )}
            <div className="space-y-1">
              <span className="text-sm text-muted-foreground">Version</span>
              <Select
                value={versionId}
                onValueChange={setVersionId}
                disabled={!appId}
              >
                <SelectTrigger size="sm" className="w-full">
                  <SelectValue
                    placeholder={
                      appId ? "Select version…" : "Select app first…"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {versions.map((v: Version) => (
                    <SelectItem key={v.id} value={v.id}>
                      {(v.docName || v.versionName || v.id) +
                        ` · ${
                          (States as Record<string, { name?: string }>)[
                            v.state!
                          ]?.name ?? v.state
                        }`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {appId && vdata && versions.length === 0 && (
                <span className="text-xs text-muted-foreground">
                  No open versions — create one first.
                </span>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!versionId || isMutating}
              onClick={() => link({ versionId })}
            >
              {isMutating ? "上车中…" : "上车"}
            </Button>
          </DialogFooter>
        </DialogContent>
    </Dialog>
  );
};

export default ScheduleVersionDialog;
